import {
  isAllowedValue,
  removeProperties,
  setRecentSearchQuery,
} from "../../../../services";
import {
  getUrlParams,
  stringifyURI,
} from "../../../../services/utils";
import {
  AdsSortOption,
  ModuleNameHr,
  PageRoute,
  QueryParam
} from "../../../../models/enums";
import { mapCategoryToEn, mapModuleToEn } from "../../../LanguageMapper";
import history from "../../../../history";
import { subcategoriesKey } from "../../../Configuration/configuration.ducks";
import {
  buildNodeRoutePath,
  findMake,
  findMakeByModel,
  findModel
} from "../../../Configuration/helpers";

const single = value => (Array.isArray(value) ? value[0] : value);

// A search over several makes, models or locations has no slug of its own: the
// url stays on the category, whose SEO route (H1, meta title) covers them all
const isMultiple = value => Array.isArray(value) && value.length > 1;

// Url slug of the selected city or county: the resolved location of the id
// (configuration/locations/resolve) - "" while it is not loaded yet.
const getLocationSlug = (resolvedLocations, { includeCityIds, includeCountyIds }) => {
  if (isMultiple(includeCityIds) || isMultiple(includeCountyIds)) return "";

  const id = single(includeCityIds) || single(includeCountyIds);
  const resolved = id && resolvedLocations ? resolvedLocations[id] : null;

  return resolved && resolved.route ? resolved.route : "";
};

// A make or model offered only in some subcategories (commercial vehicles) has its
// SEO route under the subcategory, so it gets a slug only for exactly that subcategory
const offeredInSubcategory = (node, categoryLv1Ids) => {
  if (!node.categoryLv1Ids || node.categoryLv1Ids.length === 0) return true;
  if (!categoryLv1Ids || isMultiple(categoryLv1Ids)) return false;

  const selected = String(single(categoryLv1Ids)).toLowerCase();

  return node.categoryLv1Ids.some(id => id.toLowerCase() === selected);
};

// "bmw" or "bmw/x5" from the makes of the category
const getMakeSlug = (makes, { includeMakeIds, includeModelIds, categoryLv1Ids }) => {
  if (!makes) return "";
  if (isMultiple(includeMakeIds) || isMultiple(includeModelIds)) return "";

  const modelId = single(includeModelIds);
  const makeId = single(includeMakeIds);

  if (modelId) {
    const make = makeId ? findMake(makes, makeId) : findMakeByModel(makes, modelId);
    const model = findModel(make ? [make] : makes, modelId);

    if (!make || !model) return "";
    if (!offeredInSubcategory(make, categoryLv1Ids) || !offeredInSubcategory(model, categoryLv1Ids)) return "";

    return `${make.route}/${model.route}`;
  }

  if (makeId) {
    const make = findMake(makes, makeId);

    return make && offeredInSubcategory(make, categoryLv1Ids) ? make.route : "";
  }

  return "";
};

// "strucna/informatika" from the subcategory tree of the module / category;
// several selected subcategories have no slug of their own, like several makes
const getSubcategorySlug = (subcategories, { categoryLv1Ids, categoryLv2Ids }) => {
  if (!subcategories) return "";
  if (isMultiple(categoryLv1Ids) || isMultiple(categoryLv2Ids)) return "";

  const id = single(categoryLv2Ids) || single(categoryLv1Ids);

  return (id && buildNodeRoutePath(subcategories, id)) || "";
};


// Drop tracking when filters/sort change; pagination (and module en/hr
// normalization) is the only allowed delta. Empty values are excluded so
// that handleOnSubmit's undefined `text`/`category` spread doesn't look
// like a diff vs the parsed URL JSON.
const filterSignature = (obj: any) => {
  if (!obj) return "";
  const { page, module, useRelevancyScoreSort, sortOption, ...rest } = obj;
  return JSON.stringify(
    Object.keys(rest)
      .filter(k => isAllowedValue(rest[k]))
      .sort()
      .map(k => [k, rest[k]])
  );
};

export const useUpdateUrlParams = (props) => {
  const updateUrlParams = queryParams => {
    const {
      module,
      location: { search, pathname },
      controlFields,
      configuration,
    } = props;

    // Preserve search tracking params (search_type and search_query) only if search text hasn't changed
    const currentUrlParams = new URLSearchParams(search);
    const searchType = currentUrlParams.get("search_type");
    const searchQuery = currentUrlParams.get("search_query");

    // Only preserve tracking params if the search text matches the original query
    const text = queryParams.text || queryParams.searchText
    const currentSearchText = text ? text.trim().toLowerCase() : "";
    const originalSearchQuery = searchQuery ? decodeURIComponent(searchQuery).trim().toLowerCase() : "";
    const userTypedDifferentText =
      currentSearchText !== "" && currentSearchText !== originalSearchQuery;


    const previousFilters = getUrlParams(search, QueryParam.searchQuery);
    const filtersChanged =
      filterSignature(previousFilters) !== filterSignature(queryParams);

    const shouldPreserveTracking =
      !!searchType && !userTypedDifferentText && !filtersChanged;

    const appendTrackingParams = (url: string): string => {
      if (!shouldPreserveTracking) return url;
      const separator = url.includes("?") ? "&" : "?";
      let result = `${url}${separator}search_type=${searchType}`;
      if (searchQuery) {
        result += `&search_query=${encodeURIComponent(decodeURIComponent(searchQuery))}`;
      }
      return result;
    };

    let filteredParams = queryParams;

    let modulePased = false;

    // Checking if the module is set and depending on that state
    // we are going to show certain filters for certain module
    if (decodeURIComponent(search).split(",").length > 2) {
      let temp = decodeURIComponent(search).split(",");
      for (let i = 0; i < temp.length; i++) {
        if (temp[i].includes("module")) {
          modulePased = true;
        }
      }
    }

    if (!modulePased) {
      if (!module && !queryParams.category) {
        filteredParams = removeProperties(filteredParams, "category");
      }

      if (!module && !queryParams.module) {
        filteredParams = removeProperties(filteredParams, "module");
      }
    }

    const queryParamsKeys = Object.keys(filteredParams);

    if (
      queryParamsKeys.length > 0 &&
      queryParamsKeys.some(field => isAllowedValue(filteredParams[field]))
    ) {

      // useRelevancyScoreSort tracks the "Najrelevantniji" sort: true exactly
      // when there's text to score against and that sort is selected. This
      // keeps the flag aligned with the user's sort choice through pagination,
      // sort changes (both away from and back to mostRelevant), and filter
      // tweaks on an AI search.
      const useRelevancyScoreSort =
        !!currentSearchText && filteredParams.sortOption === AdsSortOption.mostRelevant;
      filteredParams = { ...filteredParams, useRelevancyScoreSort };
      const queryUrl = stringifyURI(filteredParams, QueryParam.searchQuery, true);
      setRecentSearchQuery(filteredParams.category, filteredParams, controlFields);

      // if (search !== queryUrl) {
      //   history.push(`/${module}${PageRoute.search}${queryUrl}`);
      // }
      const {
        category,
        includeCityIds,
        includeCountyIds,
        includeMakeIds,
        includeModelIds,
        categoryLv1Ids,
        categoryLv2Ids,
      } = filteredParams;

      let location = "";
      let make = "";
      let categoryLv = "";

      const categoryEn = mapCategoryToEn(category, mapModuleToEn(module));

      if (includeCityIds || includeCountyIds) {
        location = getLocationSlug(
          configuration && configuration.resolvedLocations,
          filteredParams
        );
      }

      if (includeMakeIds || includeModelIds) {
        make = getMakeSlug(
          configuration && configuration.makes[categoryEn],
          filteredParams
        );
      }

      if (categoryLv1Ids || categoryLv2Ids) {
        categoryLv = getSubcategorySlug(
          configuration && configuration.subcategories[subcategoriesKey(module, categoryEn)],
          filteredParams
        );
      }

      if (module) {
        if (pathname.includes(ModuleNameHr.realEstate)) {
          history.push(
            appendTrackingParams(`${module ? `/${module}` : ""}${category ? `/${category}` : ""}${location ? `/${location}` : ""
              }${PageRoute.search}${queryUrl}`)
          );
        } else if (pathname.includes(ModuleNameHr.vehicles) || pathname.includes(ModuleNameHr.machinesAndTools)) {
          history.push(
            appendTrackingParams(`${module ? `/${module}` : ""}${category ? `/${category}` : ""}${categoryLv ? `/${categoryLv}` : ""}${make ? `/${make}` : ""}${PageRoute.search}${queryUrl}`)
          );
        } else if (pathname.includes(module)) {
          history.push(
            appendTrackingParams(`${module ? `/${module}` : ""}${category ? `/${category}` : ""}${categoryLv ? `/${categoryLv}` : ""}${PageRoute.search}${queryUrl}`)
          );
        } else if (search && search !== queryUrl) {
          history.push(
            appendTrackingParams(`${module ? `/${module}` : ""}${PageRoute.search}${queryUrl}`)
          );
        }
      } else if (pathname == PageRoute.search) {
        history.push(appendTrackingParams(`${PageRoute.search}${queryUrl}`));
      } else if (pathname != PageRoute.search) {
        history.replace("/");
      }
    } else if (search) {
      // @ts-ignore
      history.replace();
    }
  };

  return updateUrlParams

}


// WEBPACK FOOTER //
// ./src/routes/Search/HOCs/hooks/useUpdateUrlParams.ts